/**
 * Clip-to-Region Operation — clip a line layer to within a region boundary.
 *
 * The second geolibre-backed capability (after buffer_zone). The LLM picks the
 * `clip_to_region` capability; the executor compiles it to a geolibre-wasm
 * `line_polygon_clip` call. e.g. "show rivers inside Kenya" → only the river
 * segments that fall within Kenya's border, cut exactly at the boundary.
 *
 * Where point_in_region answers "which POINTS are inside a region", this answers
 * "what does a LINE layer look like clipped to a region" — it cuts crossing
 * features at the border rather than keeping/dropping whole features.
 *
 * CRS note: unlike buffer_zone, clipping is purely topological, so it is valid
 * directly on lon/lat — no projection round-trip and no CRS metadata required
 * (verified against geolibre line_polygon_clip with Multi* geometries).
 *
 * Step shape (produced by the parser, validated before execution):
 *   { op: 'clip_to_region', target: '<line layer id>', region: '<place>',
 *     admin_level?: 'ADM0'..'ADM5', speech?: string }
 */

import { loadDataset } from '../../data/loader.js'
import { filterByCountry, filterByContinent } from '../../data/natural-earth.js'
import { applyPhysicalContinentConvention } from '../../utils/physical-continents.js'
import { flyToBbox } from '../pipeline-utils.js'
import { createLogger } from '../../utils/logger.js'
import { R2_BASE_URL } from '../../config.js'
import { deserialize as fgbDeserialize } from 'flatgeobuf/lib/mjs/geojson.js'
import { startRiverLod, teardownActiveRiverLod, bboxArea, AREA_SMALL_REGION } from './rivers-lod.js'

const log = createLogger('clip-to-region')

export const meta = {
  name: 'clip_to_region',
  category: 'analytics',
  inputs: ['services.layers', 'services.speech', 'services.adminBoundaries'],
  outputs: ['resultCount'],
}

export const CAPABILITY = {
  id: 'clip_to_region',
  display_name: 'Clip to Region',
  backend: 'geolibre:line_polygon_clip',
  params: [
    { name: 'target', io_role: 'input', data_kind: 'vector', geometry: 'line', required: true },
    { name: 'region', io_role: 'input', data_kind: 'string', required: true },
    { name: 'admin_level', data_kind: 'string', required: false },
  ],
  output: { io_role: 'output', data_kind: 'vector', geometry: 'line' },
}

// Line layers that can be clipped (v1: rivers). Roads / coastlines / polygon
// layers are a follow-up — geolibre clips them too (line_polygon_clip for lines,
// `clip` for polygons).
const TARGET_LAYERS = {
  rivers: {
    dataset: 'rivers',
    resolution: 'medium',
    riverOnly: true,
    render: 'rivers',
    // Dense source: HydroRIVERS v10 reaches, read by bounding box from a single
    // spatially-indexed FlatGeobuf on R2 via HTTP range requests. Falls back to
    // the bundled Natural Earth `dataset` when the .fgb is absent/unreachable.
    fgbUrl: `${R2_BASE_URL}/rivers/hydrorivers.fgb`,
    // Coarse tier (major rivers only, ORD_FLOW ≤ 3) for zoom-dependent LOD on
    // mega-basins: shown zoomed-out; detail (fgbUrl) is read per-viewport on
    // zoom-in. Optional — when this file isn't on R2 the op behaves as before.
    fgbUrlCoarse: `${R2_BASE_URL}/rivers/hydrorivers_coarse.fgb`,
  },
}

/** Catalog of clippable layers, in the shape the validator expects. */
export function clipCatalog() {
  const catalog = {}
  for (const id of Object.keys(TARGET_LAYERS)) catalog[id] = { exists: true }
  return catalog
}

/** Coerce a free-form admin level to a canonical ADM0..ADM5 (default ADM0). Pure. */
export function normalizeAdminLevel(level) {
  if (!level) return 'ADM0'
  const m = String(level)
    .toUpperCase()
    .match(/ADM[0-5]/)
  return m ? m[0] : 'ADM0'
}

/** Deterministic contract validator. Pure — exported for testing. */
export function validateClipStep(step = {}, catalog = {}) {
  const errors = []
  if (!step.target) {
    errors.push({ field: 'target', reason: 'target layer is required' })
  } else if (!catalog[step.target] || catalog[step.target].exists === false) {
    errors.push({ field: 'target', reason: `layer '${step.target}' cannot be clipped yet` })
  }
  if (!step.region || !String(step.region).trim()) {
    errors.push({ field: 'region', reason: 'a region (country or admin area) is required' })
  }
  return { ok: errors.length === 0, errors, warnings: [] }
}

/** Keep only river lines (drop lake centerlines), matching showRivers' filter. Pure. */
export function filterRiverFeatures(features) {
  return (Array.isArray(features) ? features : []).filter(
    (f) => (f?.properties?.featurecla || 'River') === 'River'
  )
}

/**
 * Axis-aligned bounding box of every coordinate in a FeatureCollection.
 * Walks arbitrarily nested coordinate arrays (Point → MultiPolygon). Pure.
 *
 * @param {object} fc — GeoJSON FeatureCollection (lon/lat)
 * @returns {{minX:number,minY:number,maxX:number,maxY:number}|null} null if empty
 */
export function bboxOfFeatureCollection(fc) {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  const walk = (coords) => {
    if (!Array.isArray(coords)) return
    if (typeof coords[0] === 'number') {
      const [x, y] = coords
      if (x < minX) minX = x
      if (y < minY) minY = y
      if (x > maxX) maxX = x
      if (y > maxY) maxY = y
      return
    }
    for (const c of coords) walk(c)
  }

  for (const f of fc?.features || []) walk(f?.geometry?.coordinates)

  return Number.isFinite(minX) ? { minX, minY, maxX, maxY } : null
}

/**
 * Map a HydroRIVERS ORD_FLOW (logarithmic discharge class, lower = bigger river)
 * to a Natural-Earth-style `scalerank` (1 = major … 6 = minor) so the existing
 * showRivers line-width interpolation draws big rivers thicker. Pure.
 */
export function ordFlowToScalerank(ordFlow) {
  const v = Number(ordFlow)
  if (!Number.isFinite(v)) return 4
  return Math.max(1, Math.min(6, Math.round(v) - 1))
}

/**
 * Clip a line FeatureCollection to a region polygon FeatureCollection via
 * geolibre line_polygon_clip. Topological — no projection/CRS needed. Crossing
 * lines are cut at the boundary; outside lines are dropped; properties survive.
 *
 * @param {object} linesFC — GeoJSON FeatureCollection of lines (lon/lat)
 * @param {object} regionFC — GeoJSON FeatureCollection of clip polygons (lon/lat)
 * @param {{ runVectorTool?:Function }} [deps] — inject for testing
 * @returns {Promise<object>} clipped line FeatureCollection
 */
export async function clipLinesToRegion(linesFC, regionFC, deps = {}) {
  const lines = linesFC?.features || []
  const region = regionFC?.features || []
  const empty = { type: 'FeatureCollection', features: [] }
  if (!lines.length || !region.length) return empty

  const runVectorTool =
    deps.runVectorTool || (await import('../../data/geolibre-engine.js')).runVectorTool

  const clipped = await runVectorTool(
    'line_polygon_clip',
    ['--input=/work/in.geojson', '--clip=/work/clip.geojson', '--output=/work/out.geojson'],
    { 'in.geojson': linesFC, 'clip.geojson': regionFC }
  )
  return clipped && Array.isArray(clipped.features) ? clipped : empty
}

// US state names + postal abbreviations → full state name, for resolving the
// parent in queries like "DEM of Orange County, CA" on the regex fast path
// (the Haiku path carries an explicit country). Deliberately US-only: the
// dominant classroom case; everywhere else the AI path supplies the country.
export const US_STATES = {
  al: 'Alabama',
  ak: 'Alaska',
  az: 'Arizona',
  ar: 'Arkansas',
  ca: 'California',
  co: 'Colorado',
  ct: 'Connecticut',
  de: 'Delaware',
  fl: 'Florida',
  ga: 'Georgia',
  hi: 'Hawaii',
  id: 'Idaho',
  il: 'Illinois',
  in: 'Indiana',
  ia: 'Iowa',
  ks: 'Kansas',
  ky: 'Kentucky',
  la: 'Louisiana',
  me: 'Maine',
  md: 'Maryland',
  ma: 'Massachusetts',
  mi: 'Michigan',
  mn: 'Minnesota',
  ms: 'Mississippi',
  mo: 'Missouri',
  mt: 'Montana',
  ne: 'Nebraska',
  nv: 'Nevada',
  nh: 'New Hampshire',
  nj: 'New Jersey',
  nm: 'New Mexico',
  ny: 'New York',
  nc: 'North Carolina',
  nd: 'North Dakota',
  oh: 'Ohio',
  ok: 'Oklahoma',
  or: 'Oregon',
  pa: 'Pennsylvania',
  ri: 'Rhode Island',
  sc: 'South Carolina',
  sd: 'South Dakota',
  tn: 'Tennessee',
  tx: 'Texas',
  ut: 'Utah',
  vt: 'Vermont',
  va: 'Virginia',
  wa: 'Washington',
  wv: 'West Virginia',
  wi: 'Wisconsin',
  wy: 'Wyoming',
  dc: 'District of Columbia',
}
const US_STATE_NAMES = new Set(Object.values(US_STATES).map((s) => s.toLowerCase()))

// High-res ADM0 upgrade threshold (degrees of mainland span). Matches the
// on-demand DEM builder's MAX_SPAN_DEG: countries at or under it get sharp
// ~30-300 m/px DEMs where border fidelity is visible; above it the raster is
// km/px continent-COG data and Natural Earth 50m is indistinguishable.
// (Duplicated from ops/terrain-analysis.js ON_DEMAND_LIMITS to avoid an
// import cycle — terrain-analysis imports this module.)
const HIGHRES_ADM0_MAX_SPAN_DEG = 12

/** Larger axis of a FeatureCollection's bbox in degrees, or null. Pure. */
function neSpanDeg(fc) {
  const b = bboxOfFeatureCollection(fc)
  if (!b) return null
  return Math.max(b.maxX - b.minX, b.maxY - b.minY)
}

/** Resolve a spoken parent ("CA", "California", "Brazil") for a division query.
 *  Returns { iso3, parentDivision } — parentDivision is set when the parent is
 *  itself a sub-national division (used to disambiguate the child). Pure given
 *  an adminBoundaries with getCountry. Exported for tests. */
export function resolveDivisionParent(parentName, adminBoundaries) {
  const raw = String(parentName || '').trim()
  if (!raw) return { iso3: null, parentDivision: null }
  const key = raw.toLowerCase().replace(/^the\s+/, '')
  // US states FIRST: two-letter postal codes collide with ISO codes ("CA" is
  // Canada's ISO-2), and a bare state name must not fuzzy-match a country.
  if (US_STATES[key]) return { iso3: 'USA', parentDivision: US_STATES[key] }
  if (US_STATE_NAMES.has(key)) return { iso3: 'USA', parentDivision: raw }
  const country = adminBoundaries?.getCountry?.(raw)
  if (country?.iso3) return { iso3: country.iso3, parentDivision: null }
  return { iso3: null, parentDivision: raw }
}

/**
 * Resolve a SINGLE named sub-national division ("São Paulo", "Orange County")
 * to a one-feature FeatureCollection, or null. Needs a country to search in —
 * from opts.countryIso3 (the AI path emits it) or opts.parentName (resolved
 * via resolveDivisionParent; "CA"/"California" → USA). Tries the hinted admin
 * level first, then ADM1→ADM3, using the same findDivisionInParent machinery
 * show_single_division trusts (name-fuzzy + centroid-in-parent disambiguation).
 */
export async function resolveSingleDivision(name, adminBoundaries, opts = {}) {
  if (!name || !adminBoundaries?.findDivisionInParent) return null
  const { iso3: parentIso3, parentDivision } = resolveDivisionParent(
    opts.parentName,
    adminBoundaries
  )
  let iso3 = opts.countryIso3 || parentIso3
  let adminLevel = opts.adminLevel
  // No hint at all, but the name itself is a US state ("DEM of California") —
  // the single most common sub-national classroom query.
  if (!iso3) {
    const key = String(name)
      .toLowerCase()
      .replace(/^the\s+/, '')
    if (US_STATE_NAMES.has(key) || US_STATES[key]) {
      iso3 = 'USA'
      adminLevel = adminLevel && /^ADM[1-5]$/i.test(adminLevel) ? adminLevel : 'ADM1'
    }
  }
  if (!iso3) return null

  const hinted = /^ADM[1-5]$/i.test(String(adminLevel || ''))
    ? String(adminLevel).toUpperCase()
    : null

  // MOST-DETAILED FIRST (CL-233): the Overture/geoBoundaries-HP boundary
  // parquet (ADM1-2 Overture, ADM3-5 High-Precision) beats the legacy
  // simplified geoBoundaries polygons everywhere it exists — this is what
  // makes "DEM of Ramsey County" clip to the real county shape instead of
  // the blocky legacy one. Single-feature DuckDB range read; admLevel null
  // matches by name across levels; the parent division ("Minnesota") is
  // resolved to its ISO-3166-2 region for disambiguation inside the service.
  // Fails soft (parquet missing / DuckDB not ready) → legacy path below.
  const highRes =
    opts._getDivisionPolygon /* test seam */ ||
    (await import('../../data/boundary-feature-service.js').then(
      (m) => m.getDivisionPolygon,
      () => null
    ))
  if (highRes) {
    try {
      const f = await highRes(iso3, hinted, name, parentDivision)
      if (f?.geometry) {
        return {
          fc: { type: 'FeatureCollection', features: [f] },
          iso3,
          name: f.properties?.shapeName || f.properties?.name_en || name,
          admLevel: hinted || null,
          kind: 'division',
          highRes: true,
        }
      }
    } catch {
      /* fall through to legacy resolution */
    }
  }

  // A division-parent ("Orange County, CA") implies the child is BELOW ADM1.
  const levels = hinted ? [hinted] : parentDivision ? ['ADM2', 'ADM3'] : ['ADM1', 'ADM2', 'ADM3']

  for (const lvl of levels) {
    try {
      const f = await adminBoundaries.findDivisionInParent(iso3, lvl, name, parentDivision)
      if (f?.geometry) {
        return {
          fc: { type: 'FeatureCollection', features: [f] },
          iso3,
          name: f.properties?.shapeName || f.properties?.name_en || name,
          admLevel: lvl,
          kind: 'division',
        }
      }
    } catch {
      // level missing for this country — try the next one
    }
  }
  return null
}

/**
 * Resolve a place name to a region polygon FeatureCollection. Returns
 * { fc, iso3, name, kind? } or null when unavailable. deps ({ adminBoundaries,
 * countriesData }) are injected (ctx services) so this is testable without
 * network.
 *
 * Source by kind:
 *  - ADM1+ (sub-national, whole level): geoBoundaries population-enriched
 *    polygons (R2).
 *  - ADM0 (country outline, the default): the geoBoundaries manifest omits ADM0,
 *    so we use the already-loaded Natural Earth country polygons instead. Falls
 *    back to geoBoundaries at the requested level, then to an ADM1 union.
 *  - CONTINENT ("South America", "Africa", …): the union of Natural Earth
 *    country polygons on that continent — so a continent-scale raster can be
 *    masked to the coastline, land only (CL-227). "Australia" resolves as the
 *    COUNTRY first (that is what students mean), never the Oceania continent.
 *  - SINGLE DIVISION ("São Paulo" + country/parent hints via opts): one
 *    named ADM1–ADM3 polygon, see resolveSingleDivision.
 *
 * opts ({ parentName, countryIso3 }) carry the AI/fast-path hints for the
 * single-division case; existing 3-arg callers are unaffected.
 */
export async function resolveRegion(region, adminLevel, deps = {}, opts = {}) {
  let { adminBoundaries, countriesData } = deps
  if (!region) return null

  // The clip MASK wants a smooth coastline: the app preloads 110m ('low')
  // countries for fast startup, which is too blocky to clip against, so the
  // ADM0 path below loads 50m ('medium') instead (loadDataset caches it after
  // the first call). Injectable for tests. (CL-120)
  const loadCountries = deps.loadCountries || (() => loadDataset('countries', 'medium'))

  // Both dependencies load asynchronously (admin metadata + the Natural Earth
  // countries GeoJSON). A query fired before they finish would otherwise get a
  // null country / empty countriesData and skip resolution entirely — leaving,
  // e.g., a Voronoi unclipped. So: fall back to the admin singleton if services
  // didn't supply it, await its readiness (init is idempotent), and lazy-load
  // the country polygons on demand if they aren't ready yet. (CL-116)
  if (!adminBoundaries) {
    try {
      adminBoundaries = (await import('../../data/admin-boundaries.js')).adminBoundaries
    } catch {
      /* ignore */
    }
  }
  if (adminBoundaries && !adminBoundaries.isReady && typeof adminBoundaries.init === 'function') {
    try {
      await adminBoundaries.init()
    } catch {
      /* ignore — resolution below still degrades gracefully */
    }
  }

  let iso3 = null
  let name = region
  const country = adminBoundaries?.getCountry?.(region)
  if (country?.iso3) {
    iso3 = country.iso3
    name = country.name || region
  }

  // A sub-national hint + an explicit country that DISAGREES with the country
  // match means the name is a division that collides with a country name
  // ("Georgia" the US state vs Georgia the country) — resolve the division.
  const hintedLevel = String(opts.adminLevel || adminLevel || '')
  if (/^ADM[1-5]$/i.test(hintedLevel) && opts.countryIso3 && (!iso3 || opts.countryIso3 !== iso3)) {
    const div = await resolveSingleDivision(region, adminBoundaries, {
      adminLevel: hintedLevel,
      parentName: opts.parentName,
      countryIso3: opts.countryIso3,
      _getDivisionPolygon: opts._getDivisionPolygon,
    })
    if (div) return div
  }

  // Sub-national levels → geoBoundaries.
  if (iso3 && adminLevel && adminLevel !== 'ADM0' && adminBoundaries?.getBoundaries) {
    const fc = await adminBoundaries.getBoundaries(iso3, adminLevel)
    if (fc?.features?.length) return { fc, iso3, name }
  }

  // Country outline (ADM0). Boundary detail follows raster detail (CL-233):
  //  - SMALL countries (mainland span ≤ HIGHRES_ADM0_MAX_SPAN_DEG) get the
  //    high-res Overture ADM0 outline from the boundary parquet — these are
  //    exactly the countries whose DEMs build on demand at ~30-300 m/px,
  //    where the Natural Earth 50m border is visibly blocky.
  //  - LARGE countries keep Natural Earth 50m: their DEMs come from km/px
  //    continent COGs, where extra border detail is invisible but the
  //    ultra-detailed geometry is a many-MB download.
  // Both paths fail soft into the next.
  if (iso3) {
    let countries = null
    try {
      countries = await loadCountries()
    } catch {
      countries = null
    }
    if (!countries?.features?.length) countries = countriesData
    if (countries?.features?.length) {
      const fc = filterByCountry(countries, iso3)
      if (fc?.features?.length) {
        const span = neSpanDeg(fc)
        if (span != null && span <= HIGHRES_ADM0_MAX_SPAN_DEG) {
          try {
            const getCountry =
              opts._getCountryPolygon /* test seam */ ||
              (await import('../../data/boundary-feature-service.js').then(
                (m) => m.getCountryPolygon,
                () => null
              ))
            const f = getCountry ? await getCountry(iso3) : null
            if (f?.geometry) {
              return {
                fc: { type: 'FeatureCollection', features: [f] },
                iso3,
                name,
                kind: 'country',
                highRes: true,
              }
            }
          } catch {
            /* fall through to Natural Earth */
          }
        }
        return { fc, iso3, name }
      }
    }
  }

  // Fallbacks: geoBoundaries at the requested level, then an ADM1 union.
  if (iso3 && adminBoundaries?.getBoundaries) {
    for (const lvl of [adminLevel, 'ADM1']) {
      if (!lvl) continue
      const fc = await adminBoundaries.getBoundaries(iso3, lvl)
      if (fc?.features?.length) return { fc, iso3, name }
    }
  }

  // Not a country. CONTINENT? Union of Natural Earth country polygons on the
  // continent (or a named subregion like "Central America") — this is what
  // lets "DEM of South America" mask to the coastline instead of spilling
  // into the Atlantic (CL-227). Reached only when the country match failed,
  // so "Australia" stays the country.
  if (!iso3) {
    try {
      const countries = await loadCountries()
      if (countries?.features?.length) {
        const cname = String(region)
          .trim()
          .replace(/^the\s+/i, '')
        let fc = filterByContinent(countries, cname)
        if (!fc?.features?.length) {
          // Named-subregion sets are keyed in Title Case; retry to match.
          const tc = cname.replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
          fc = filterByContinent(countries, tc)
        }
        // ≥2 features: a real multi-country region, not a stray attribute match.
        if (fc?.features?.length >= 2) {
          // PHYSICAL convention for the clip mask (CL-237): Russia splits at
          // the Ural meridian — its European part stays in 'Europe', Siberia
          // joins 'Asia'. Mask-path only; attribute choropleths keep Natural
          // Earth's country-level continent membership (whole Russia=Europe).
          const phys = applyPhysicalContinentConvention(fc, cname, countries)
          return { fc: phys, iso3: null, name: cname, kind: 'continent' }
        }
      }
    } catch {
      /* fall through to division resolution */
    }

    // SINGLE DIVISION ("São Paulo", "Orange County") using whatever hints are
    // available: explicit country (AI path), a parent name ("…, CA"), the
    // division being a US state itself, or the caller's context country.
    const div = await resolveSingleDivision(region, adminBoundaries, {
      adminLevel: hintedLevel,
      parentName: opts.parentName,
      countryIso3: opts.countryIso3 || opts.contextIso3 || null,
      _getDivisionPolygon: opts._getDivisionPolygon,
    })
    if (div) return div
  }

  return null
}

/** Build the spoken narration. Exported for testing. */
export function clipSpeech(step, count) {
  if (step.speech) return step.speech
  const n = count | 0
  const where = step.region ? ` in ${step.region}` : ''
  if (!n) return `I couldn't find any ${step.target}${where}.`
  return `Here are the ${step.target} clipped to${where} — ${n} segment${n === 1 ? '' : 's'}.`
}

// ── Execution path ──────────────────────────────────────────────────

const _cache = new Map()

async function loadTarget(id) {
  const def = TARGET_LAYERS[id]
  if (!def) throw new Error(`clip_to_region: unknown target layer '${id}'`)
  if (_cache.has(id)) return _cache.get(id)
  const fc = await loadDataset(def.dataset, def.resolution)
  let features = Array.isArray(fc?.features) ? fc.features : []
  if (def.riverOnly) features = filterRiverFeatures(features)
  _cache.set(id, features)
  return features
}

/**
 * Read HydroRIVERS reaches inside `bbox` from the global FlatGeobuf on R2 via
 * HTTP range requests — only the features in the box transfer (a few hundred KB),
 * not the whole file. Each reach is unnamed and carries no `featurecla`, so we
 * synthesize `featurecla: 'River'` (else showRivers' filter drops it) and a
 * `scalerank` from ORD_FLOW (for line width), preserving the original props.
 *
 * Returns the feature array, or null on HEAD failure / read error so the caller
 * can fall back to the bundled Natural Earth rivers. fetch/deserialize are
 * injectable for unit testing (no network in tests).
 *
 * @param {{minX,minY,maxX,maxY}} bbox
 * @param {{ url?:string, headCheck?:Function, deserialize?:Function }} [deps]
 * @returns {Promise<object[]|null>}
 */
export async function loadRiversFromFgb(bbox, deps = {}) {
  if (!bbox) return null
  const url = deps.url || TARGET_LAYERS.rivers.fgbUrl
  const headCheck = deps.headCheck || ((u) => fetch(u, { method: 'HEAD' }))
  const deserialize = deps.deserialize || fgbDeserialize

  try {
    // Fast-path probe: a definitive 404 means the file isn't there, so fall back
    // immediately. ANY other outcome — 200, a transient 5xx, or a thrown/opaque
    // error — proceeds to the real range read. A flaky HEAD must not force a
    // false fallback to the sparse Natural Earth set (observed once during an
    // ev-v6→ev-v7 service-worker transition, where the old SW 503'd the HEAD).
    try {
      const head = await headCheck(url)
      if (head && head.status === 404) return null
    } catch {
      // ignore — the range read below has its own error handling
    }

    const rect = { minX: bbox.minX, minY: bbox.minY, maxX: bbox.maxX, maxY: bbox.maxY }
    const features = []
    for await (const feature of deserialize(url, rect)) {
      if (!feature?.geometry) continue
      const props = feature.properties || {}
      feature.properties = {
        ...props,
        featurecla: 'River',
        scalerank: ordFlowToScalerank(props.ORD_FLOW),
      }
      features.push(feature)
    }
    return features
  } catch (err) {
    log.warn('HydroRIVERS .fgb unavailable; falling back to Natural Earth rivers', err)
    return null
  }
}

/**
 * Pick the densest available source for a clippable layer: the HydroRIVERS .fgb
 * (read by the region's bbox) when the layer declares one and it yields features,
 * otherwise the bundled Natural Earth dataset. Exported with injectable loaders
 * so the selection logic is unit-testable without network. Returns a feature array.
 */
export async function loadClipFeatures(step, region, deps = {}) {
  const def = TARGET_LAYERS[step.target] || {}
  const fromFgb = deps.loadRiversFromFgb || loadRiversFromFgb
  const fromTarget = deps.loadTarget || loadTarget

  if (def.fgbUrl) {
    const bbox = bboxOfFeatureCollection(region?.fc)
    if (bbox) {
      const fgbFeatures = await fromFgb(bbox, deps.fgbDeps)
      if (Array.isArray(fgbFeatures) && fgbFeatures.length) {
        log.info(`HydroRIVERS: ${fgbFeatures.length} reaches in bbox for ${region?.name}`)
        return fgbFeatures
      }
    }
  }
  return fromTarget(step.target)
}

/** Wrap a feature array as a FeatureCollection. */
const FC = (features) => ({ type: 'FeatureCollection', features: features || [] })

/** Draw clipped rivers via the layer manager (single place so tiers render alike). */
function renderRivers(layers, clippedFC, opts = {}) {
  layers?.showRivers?.(clippedFC, {
    clearExisting: true,
    showLabels: opts.showLabels ?? true,
    includeLakes: false,
  })
}

export async function execute(step, ctx) {
  const { layers, speech, adminBoundaries, countriesData } = ctx.services || {}

  const verdict = validateClipStep(step, clipCatalog())
  if (!verdict.ok) {
    const why = verdict.errors.map((e) => `${e.field}: ${e.reason}`).join('; ')
    log.warn('rejected clip_to_region step', verdict.errors)
    speech?.speak(`I can't clip that yet — ${why}.`)
    ctx.spokeDuringRoute = true
    return
  }

  const adminLevel = normalizeAdminLevel(step.admin_level)

  try {
    const region = await resolveRegion(step.region, adminLevel, { adminBoundaries, countriesData })
    if (!region) {
      speech?.speak(`I don't have boundary data for ${step.region} yet.`)
      ctx.spokeDuringRoute = true
      ctx.resultCount = 0
      return
    }

    const def = TARGET_LAYERS[step.target]
    const regionBbox = bboxOfFeatureCollection(region.fc)

    // A new clip supersedes any previous zoom-LOD controller.
    teardownActiveRiverLod()

    // ── Zoom-dependent two-tier path (large river regions / mega-basins) ──
    // Render the coarse overview immediately (cheap even for the Amazon), then
    // load detail per-viewport as the user zooms in. Gated on the ACTUAL coarse
    // read: loadRiversFromFgb aborts only on a definitive 404 (a flaky HEAD/503
    // still proceeds to the range read, which bypasses the service worker), so a
    // non-empty result means the coarse tier is really there. When it isn't
    // (404 → null/empty) we fall through to the one-shot path unchanged.
    const isLargeRiverRegion =
      def?.render === 'rivers' &&
      def?.fgbUrlCoarse &&
      regionBbox &&
      bboxArea(regionBbox) >= AREA_SMALL_REGION
    const coarse = isLargeRiverRegion
      ? await loadRiversFromFgb(regionBbox, { url: def.fgbUrlCoarse })
      : null

    if (coarse?.length) {
      const clipped = await clipLinesToRegion(FC(coarse), region.fc)
      const count = clipped.features.length
      if (!count) {
        speech?.speak(`I couldn't find any ${step.target} in ${region.name}.`)
        ctx.spokeDuringRoute = true
        ctx.resultCount = 0
        return
      }
      renderRivers(layers, clipped)
      flyToBbox(region.fc, 5, ctx.services)

      const map = ctx.services?.animator?.globe?.map
      if (map) {
        startRiverLod({
          map,
          regionFc: region.fc,
          regionBbox,
          isLayerAlive: () => !!map.getLayer?.('data-rivers-line'),
          loadTier: async (tier, readBbox) => {
            const url = tier === 'coarse' ? def.fgbUrlCoarse : def.fgbUrl
            return (await loadRiversFromFgb(readBbox, { url })) || []
          },
          clipToRegion: (features, regionFc) => clipLinesToRegion(FC(features), regionFc),
          render: (fc) => renderRivers(layers, fc, { showLabels: false }),
        })
      }

      speech?.speak(clipSpeech({ ...step, region: region.name }, count))
      ctx.spokeDuringRoute = true
      ctx.resultCount = count
      return
    }

    // ── Default one-shot path (small regions, non-rivers, or no coarse tier) ──
    const features = await loadClipFeatures(step, region)
    if (!features.length) {
      speech?.speak(`I couldn't load the ${step.target} layer.`)
      ctx.spokeDuringRoute = true
      ctx.resultCount = 0
      return
    }

    const clipped = await clipLinesToRegion({ type: 'FeatureCollection', features }, region.fc)
    const count = clipped.features.length
    if (!count) {
      speech?.speak(`I couldn't find any ${step.target} in ${region.name}.`)
      ctx.spokeDuringRoute = true
      ctx.resultCount = 0
      return
    }

    // Render the clipped lines and zoom to the region.
    if (def.render === 'rivers') {
      renderRivers(layers, clipped)
    } else {
      layers?.showFilteredResults?.(clipped, 'line', {})
    }
    flyToBbox(region.fc, 5, ctx.services)

    speech?.speak(clipSpeech({ ...step, region: region.name }, count))
    ctx.spokeDuringRoute = true
    ctx.resultCount = count
  } catch (err) {
    log.error('clip_to_region failed', err)
    if (typeof window !== 'undefined') {
      window.__earthvoice_lastError = {
        op: 'clip_to_region',
        error: err?.message,
        stack: err?.stack,
        step,
      }
    }
    ctx.services?.onResultUpdate?.({
      type: 'error',
      message: `Clip failed: ${err?.message || err}`,
      intent: step,
    })
    speech?.speak(`Sorry, I could not clip that. ${err?.message || ''}`)
    ctx.spokeDuringRoute = true
  }
}
